import AuthenticationForm from "components/Authentication/AuthenticationForm";
import { useState } from "react";
import { isValidEmail, isValidPhone } from "components/Authentication/fieldValidators";
import { useTheme } from "@mui/material/styles";
import { Button } from "@mui/material";
import { Checkbox, FormControlLabel, Typography } from "@mui/material";

interface StepOrganisationProps {
  data: any;
  error: Record<string, string> | null;
  onNext: (values: any) => void;
  onBack: () => void;
}

export default function StepOrganisation({
  data,
  error,
  onNext,
  onBack,
}: StepOrganisationProps) {
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [localError, setLocalError] = useState<Record<string, string> | null>(null);
  const [billingConsent, setBillingConsent] = useState(false);
  const theme = useTheme();

  function handleSubmit(values: any) {
    const errors: Record<string, string> = {};

    if (!values.name?.trim()) {
      errors.name = "Organisation name is required.";
    }

    if (!values.email?.trim()) {
      errors.email = "Organisation email is required.";
    } else if (!isValidEmail(values.email)) {
      errors.email = "Please enter a valid organisation email (e.g. info@example.com).";
    }

    if (!values.phone_number?.trim()) {
      errors.phone_number = "Organisation phone number is required.";
    } else if (!isValidPhone(values.phone_number)) {
      errors.phone_number =
        "Please enter a valid phone number (digits, spaces, +, -, or parentheses).";
    }

    if (!values.address?.trim()) {
      errors.address = "Organisation address is required.";
    }

    /*
    if (!billingConsent) {
      errors.billingConsent =
        "You must agree to save a payment method for future billing.";
    }
    */

    if (Object.keys(errors).length > 0) {
      setLocalError(errors);
      return;
    }

    setLocalError(null);

    onNext({
      ...values,
      name: values.name.trim(),
      email: values.email.trim(),
      phone_number: values.phone_number.trim(),
      address: values.address.trim(),
      website: values.website?.trim() || "",
      description: values.description?.trim() || "",
      denomination: values.denomination?.trim() || "",
      logo: logoFile ?? null,
      account_type: 4,
    });
  }

  return (
    <AuthenticationForm
      title="Organisation Details"
      submitLabel="Submit"
      onBack={onBack}
      error={{ ...(error || {}), ...(localError || {}) }}
      fields={[
        { name: "name", label: "Organisation Name", required: true },
        { name: "description", label: "Description", type: "multiline", rows: 3 },
        { name: "email", label: "Organisation Email", required: true },
        { name: "phone_number", label: "Organisation Phone Number", required: true },
        { name: "website", label: "Website URL" },
        {
          name: "address",
          label: "Organisation Address",
          required: true,
          type: "multiline",
          rows: 2,
        },
        {
          name: "size_on_record",
          label: "Number of People connected with your organisation",
          type: "number",
        },
        {
          name: "size_at_service",
          label: "Typical attendance at your main gathering",
          type: "number",
        },
        { name: "denomination", label: "Denomination" },
      ]}
      initialValues={data}
      extraContent={
        <>
          {logoFile && (
            <img
              src={URL.createObjectURL(logoFile)}
              alt="Logo preview"
              style={{
                marginTop: 10,
                maxWidth: "200px",
                borderRadius: 6,
                display: "block",
              }}
            />
          )}

          <input
            type="file"
            id="organisation-logo-input"
            accept="image/*"
            style={{ display: "none" }}
            onChange={(e) => {
              const file = e.target.files?.[0] || null;
              setLogoFile(file);
            }}
          />

          {/*
          <FormControlLabel
            control={
              <Checkbox
                checked={billingConsent}
                onChange={(e) => setBillingConsent(e.target.checked)}
              />
            }
            label="I authorise Pathways to securely store my payment method with Stripe for future subscription billing if my organisation upgrades from the free plan."
          />

          */}

          {localError?.billingConsent && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              {localError.billingConsent}
            </Typography>
          )}

          <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
            You will not be charged during registration. Your organisation starts on the free plan.
          </Typography>

          <Button
            component="label"
            htmlFor="organisation-logo-input"
            variant="contained"
            sx={{
              mt: 2,
              backgroundColor: theme.custom.modules.publicModule.primary.dark,
              color: theme.custom.base.text.primary.dark,
              borderRadius: 1,
              "&:hover": {
                backgroundColor: theme.custom.modules.publicModule.primary.dark,
              },
            }}
          >
            {logoFile ? "Change Organisation Logo" : "Upload Organisation Logo"}
          </Button>
        </>
      }
      onSubmit={handleSubmit}
    />
  );
}